/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: chen
 *  创建时间: 2026-08-14 11:10
 *  文件名: src/pages/resource-preview/util.ts
 *  功能描述:
 */

import { cloneDeep, flatMap, sortBy } from 'lodash';
import {
  FollowSubResourceType,
  FollowSubResourceTypes,
  GetBasicResourceItemResponse,
  GetClassRecordResourceItemResponse,
  InternalResourceItem,
  ResourceType,
  ResourceTypeItem,
  ResourceTypeOrder,
} from './types';

// 获取备课课件资源
// 通过备课实录拉取备课实录+备课课件
const getPrepareLessonResources = (res: GetBasicResourceItemResponse) => {
  // 备课包保留的类型
  const needResourceTypes: FollowSubResourceType[] = [
    FollowSubResourceType.PrepareCourseMemoir,
    FollowSubResourceType.PrepareLessonWare,
  ];

  // 备课包内部资源。宝玉说的/曹兄说的
  const items = cloneDeep(res.internalResources || []).map((t: InternalResourceItem) => {
    return {
      id: t.id,
      name: t.name,
      files: t.fileInfos,
      resourceType: {
        code: t.resourceType,
        name: '备课包内部资源',
      },
    };
  });
  if ((res.files || []).length) {
    items.push({ ...res });
  }

  const _items = ((items || []) as any[]).filter((t) =>
    needResourceTypes.includes((FollowSubResourceTypes as any)?.[t.resourceType.code]),
  );

  const resources = sortBy(_items, [
    (t) => {
      return ResourceTypeOrder.indexOf(t.resourceType.code);
    },
    (t) => t.updateTime,
  ]);

  return resources;
};

// 根据基础资源获取课时数据，needResourceTypes保留的类型
// 课堂实录拉取（整套为一条数据）：通过备课包拉取课堂实录，教学课件，素材；普通试卷（备课包和作业/试卷关联，作业/试卷再关联到普通试卷）
const getClassRecordResources = (res: GetClassRecordResourceItemResponse) => {
  // 课堂实录保留的类型
  const needResourceTypes: FollowSubResourceType[] = [
    FollowSubResourceType.CourseMemoir,
    FollowSubResourceType.Courseware,
    FollowSubResourceType.Material,
  ];

  // 课堂实录只拉取示范课、备课实录的mp4资源
  const recordNeedResourceTypes: FollowSubResourceType[] = [FollowSubResourceType.CourseMemoir];
  // 试卷关联的普通试卷资源类型
  const examResourceTypes = [ResourceType.GENERAL_PAPER, ResourceType.GENERAL_ASSIGNMENT];
  // 试卷关联的普通试卷资源类型包含的资源类型
  const examIncludeResourceTypes: ResourceType[] = [ResourceType.DOCUMENT_PAPER];
  // 获取素材资源
  const items = cloneDeep(res.materials || []).filter((t) =>
    needResourceTypes.includes((FollowSubResourceTypes as any)?.[t?.resourceType?.code]),
  );

  const recordItems = (res.relationshipResources || []).filter((t) =>
    recordNeedResourceTypes.includes((FollowSubResourceTypes as any)?.[t?.resourceType?.code]),
  );

  if (recordItems.length) {
    items.push(...recordItems);
  }

  // 获取试卷资源
  const examItems = (res.relationshipResources || []).filter((t) =>
    examResourceTypes.includes(t?.resourceType?.code as ResourceType),
  );

  // 获取试卷关联的普通试卷资源
  const normalExamItems = flatMap(examItems, (t) => t.relationships || []).filter((t) =>
    examIncludeResourceTypes.includes((t?.resourceType as ResourceTypeItem)?.code as ResourceType),
  );

  if (normalExamItems.length) {
    items.push(...normalExamItems);
  }

  const resources = sortBy(items, [
    (t) => {
      return ResourceTypeOrder.indexOf(t.resourceType.code as ResourceType);
    },
    (t) => t.updateTime,
  ]);

  return resources;
};

export { getPrepareLessonResources, getClassRecordResources };
